<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Illustration from '@leaflink/stash/Illustration.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import RadioGroup from '@leaflink/stash/RadioGroup.vue';
  import RadioNew from '@leaflink/stash/RadioNew.vue';
  import { storeToRefs } from 'pinia';
  import { computed, ref } from 'vue';

  import { SPLIT_NAMES } from '@/constants';
  import { POS_OPTIONS, type PosOption } from '@/models/company';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { trackWithSegment } from '@/segment';
  import { sendCategoryManagementInterest } from '@/services/api/company';

  export interface CategoryManagementInterestModalProps {
    source: string;
  }

  const emit = defineEmits<{
    (e: 'close', action: 'cancel' | 'completed'): void;
    (e: 'waitlisted', selection: (typeof POS_OPTIONS)[number]): void;
  }>();

  const props = defineProps<CategoryManagementInterestModalProps>();
  const { userActiveCompany, getIsSplitEnabled } = storeToRefs(useUserStore());

  const selection = ref<PosOption | ''>('');
  const showHelp = ref(false);
  const showSuccess = ref(false);
  const isSubmitting = ref(false);

  const posWaitlistOptions = computed(() => {
    return getIsSplitEnabled.value(SPLIT_NAMES.RETAIL_CATEGORY_MANAGEMENT_SELF_SERVICE)
      ? POS_OPTIONS.filter((pos) => pos !== 'Dutchie' && pos !== 'Flowhub')
      : POS_OPTIONS;
  });

  async function handleSubmit() {
    if (!selection.value) {
      showHelp.value = true;
      return;
    }

    try {
      showHelp.value = false;
      isSubmitting.value = true;

      await sendCategoryManagementInterest(selection.value);

      emit('waitlisted', selection.value);

      trackWithSegment({ event: 'catManWaitlisted', properties: { source: props.source, posSystem: selection.value } });

      showSuccess.value = true;

      if (userActiveCompany.value?.slug) {
        window.localStorage.setItem(userActiveCompany.value?.slug, selection.value);
      }
    } finally {
      isSubmitting.value = false;
    }
  }
</script>

<template>
  <Modal
    size="narrow"
    data-test="modal|category-management-interest"
    open
    :title="i18n.t('categoryManagement.interest.title')"
    @dismiss="emit('close', 'cancel')"
  >
    <!-- Content -->
    <div v-if="!showSuccess" class="tw-flex tw-flex-col tw-gap-3">
      <h3>{{ i18n.t('categoryManagement.interest.insights') }}</h3>
      <h3>{{ i18n.t('categoryManagement.interest.instruction') }}</h3>
      <RadioGroup
        v-model="selection"
        variant="chip"
        :error-text="showHelp ? i18n.t('categoryManagement.interest.validationMsg') : ''"
        :label="i18n.t('categoryManagement.interest.posSelectionLabel')"
      >
        <RadioNew v-for="option in posWaitlistOptions" :id="option" :key="option" :label="option" :value="option" />
      </RadioGroup>
    </div>
    <div v-else>
      <Illustration name="check-circle" fill-color="green" :size="100" class="tw-bg-green-100 tw-w-full" />
      <section class="tw-py-3">
        <h3>{{ i18n.t('categoryManagement.interest.confirmation') }}</h3>
        <section class="tw-mt-6">
          <h4>{{ i18n.t('categoryManagement.interest.posSelected') }}</h4>
          <p>{{ selection }}</p>
        </section>
      </section>
    </div>
    <!-- Actions -->
    <template v-if="!showSuccess" #actions>
      <Button :disabled="isSubmitting" secondary @click="emit('close', 'cancel')">
        {{ i18n.t('common.cancel') }}
      </Button>
      <Button :disabled="isSubmitting" @click="handleSubmit">
        <Icon v-if="isSubmitting" name="working" class="fx-spin" />
        <span v-else>{{ i18n.t('categoryManagement.interest.action') }}</span>
      </Button>
    </template>
    <template v-else #actions>
      <Button class="tw-w-full" @click="emit('close', 'completed')">
        {{ i18n.t('categoryManagement.interest.completedAction') }}
      </Button>
    </template>
  </Modal>
</template>
